import { useEffect, useState } from "react";
import { useAuth } from "../../../../../modules/auth";
import { useLazyReadMultipleQuery } from "../../../../services/data/tableDataApi";
import { FieldFilterType } from "../../../../base/types";
import { useGetCourseAndSection } from "./useGetCourseAndSection";
import { IAssessment } from "./assessment";

export const useGetAssessment = () => {
    const { user, siteId } = useAuth();
    const [getRecords] = useLazyReadMultipleQuery();
    const [assessments, setAssessments] = useState<IAssessment[]>([]);
    const { data: records } = useGetCourseAndSection();

    useEffect(() => {
        if (records.length > 0) {
            getAssessments();
        }
        // eslint-disable-next-line
    }, [records]);

    const getAssessments = (): Promise<void> => {
        if (!user?.contactId) {
            return Promise.reject('User contact not found.');
        }

        // Map through routeRecords and fetch assessments for each route
        const promises = records.map(route =>
            getRecords({
                userId: user?.contactId || '',
                portalId: siteId || '',
                tableId: "mshied_assessment",
                fieldList:
                    ["slcm_assessmenttype",
                        "slcm_course",
                        "mshied_assessmentid",
                        "edtec365_sectionjson",
                        "edtec365_assessmentstarttime",
                        "edtec365_assessmentendtime",
                        "mshied_name",
                        "edtec365_examinstructions",
                    ],
                filters: [{
                    group: 0, filters: [
                        { field: "slcm_course", type: FieldFilterType.Const, value: route.courseid || '' },
                        { field: "slcm_assessmenttype", type: FieldFilterType.Const, value: "3ece5016-de4f-ef11-accd-000d3a0a5d30" } // guid is for online assessments
                    ]
                }]
            }).unwrap().then((data) => {
                // Process records to extract required fields
                const assessmentsRecords = data.records
                    .map(record => {
                        const sectionJson = record["edtec365_sectionjson"] as string;
                        const sections = JSON.parse(sectionJson || '[]') as Array<{ id: string }>;

                        // Check if any section id matches route.sectionmain
                        const isValid = sections.some(section => section.id === route.sectionmain);

                        if (isValid) {
                            return {
                                assessmentType: record["slcm_assessmenttype"] as string,
                                courseId: record["slcm_course"] as string,
                                assessmentId: record["mshied_assessmentid"] as string,
                                starttime: record["edtec365_assessmentstarttime"] as Date,
                                endtime: record["edtec365_assessmentendtime"] as Date,
                                name: record["mshied_name"] as string,
                                instruction: record["edtec365_examinstructions"] as string,
                                sectionmain: sectionJson // Or any specific value you need from the JSON
                            };
                        }

                        return null;
                    })
                    .filter(record => record !== null) as IAssessment[];

                return assessmentsRecords;
            })
        );

        return Promise.all(promises)
            .then(results => {
                // Flatten the array of arrays
                const allAssessments = results.flat();
                setAssessments(allAssessments);
            })
            .catch(error => {
                console.error('Error fetching assessments:', error);
            });
    };
    return { assessments };
};
